import { money } from '../format.js';
import Peek from '../Peek.jsx';
import { AssetEditor } from '../Editable.jsx';
import { TaxTriangle, SplitBar } from '../pictures.jsx';
import Callouts from '../Callouts.jsx';

// Where your money sits today.
//
// The tax triangle is the advisor's to draw: "that is our entire
// presentation" (Jake, 2026-09-29). So the page does not draw it for them. It
// sets out a stencil, the three corners in the order Pure draws them (tax-free
// at the top, taxable and tax-deferred below) with the figures beside each,
// and the advisor's marker does the rest. Beside it, what stands out; under
// it, Jake's own slide: what each treatment gives and what it takes.
//
// An advisor who draws the whole triangle themselves would find it already
// there, which takes the point out of drawing it (John, 2026-10-01). So the
// triangle can be turned off from the meeting menu: the three figures stay,
// in a row over the split bar, and the space the triangle took is left open
// for the marker. It is on unless turned off.

// What each treatment gives and what it takes. The page was cut to a few
// spoken points while it was presented (Jimmy, 2026-10-01). It is follow-up
// reading now: the advisor draws the triangle on the whiteboard before it, and
// this is what the client reads afterwards, so each card says the whole of it
// again and each finding carries the line that explains it (2026-10-02).
const POOLS = {
  taxFree: {
    label: 'Tax-free',
    tables: ['roth', 'hsa'],
    fill: '#2f7d4f',
    pros: ['Qualified withdrawals are never taxed', 'No required withdrawals', 'Heirs receive it free of income tax'],
    cons: ['Limits on how much goes in, and on who can contribute'],
  },
  taxable: {
    label: 'Taxable',
    tables: ['taxable', 'cashCd'],
    fill: '#6fa3cf',
    pros: ['Gains taxed at capital gains rates, 0 to 20%', 'Losses can offset gains', 'Heirs receive a step-up in basis'],
    cons: ['Interest and dividends taxed every year'],
  },
  taxDeferred: {
    label: 'Tax-deferred',
    tables: ['taxDeferred'],
    fill: '#015697',
    pros: ['Tax deduction now, and growth untaxed until withdrawal'],
    cons: ['Withdrawals taxed as ordinary income, up to 37%', 'Required withdrawals from age 73 to 75', 'Heirs pay income tax on what they inherit'],
  },
};

// A Roth and an HSA are both tax-free when the rules are met, but they are
// inherited differently: an HSA left to anyone but a spouse stops being an HSA
// and is taxable to the heir (IRS Publication 969). With an HSA in the pool the
// card says so.
function termsFor(key, pool) {
  if (key !== 'taxFree' || !pool?.hasHsa) return POOLS[key];
  return {
    pros: ['Qualified withdrawals are never taxed (for an HSA, qualified medical costs)', 'No required withdrawals', 'Roth: heirs receive it free of income tax'],
    cons: ['Limits on how much goes in, and on who can contribute', 'HSA: taxable to an heir other than a spouse'],
  };
}

// Top, left, right: the way the triangle is drawn on Pure's whiteboards.
const CORNERS = ['taxFree', 'taxable', 'taxDeferred'];
// Left to right on the page, in the order Jake's slide reads.
const CARDS = ['taxable', 'taxDeferred', 'taxFree'];

function PoolFigures({ corners, total }) {
  return (
    <div className="conv-pool-figs">
      {CARDS.map(k => {
        const c = corners.find(x => x.key === k);
        const share = c.value !== null && total > 0 ? ` · ${Math.round((c.value / total) * 100)}%` : '';
        return (
          <div className="conv-pool-fig" key={k} style={{ '--pool': POOLS[k].fill }}>
            <span>{`${POOLS[k].label}${share}`}</span>
            <strong className={c.value === null ? 'is-unknown' : ''}>
              <Peek title={`${POOLS[k].label} accounts`} editor={() => <AssetEditor only={POOLS[k].tables} />}>
                {c.value === null ? 'Not recorded' : money(c.value)}
              </Peek>
            </strong>
          </div>
        );
      })}
    </div>
  );
}

export default function MoneySits({ snapshot, callouts = [], triangle = true }) {
  const byKey = Object.fromEntries((snapshot?.pools || []).map(p => [p.key, p]));
  const total = snapshot?.total || 0;
  const corners = CORNERS.map(k => ({
    key: k,
    label: POOLS[k].label,
    value: total > 0 ? byKey[k]?.value || 0 : null,
  }));
  const side = callouts.some(c => !c.hidden);

  return (
    <>
      <div className="conv-module-head">
        <h2>Where your money sits today</h2>
        {total > 0 ? <span className="conv-quiet">{`${money(total)} in liquid assets`}</span> : null}
      </div>

      <div className={`conv-tax-row${side ? ' has-side' : ''}`}>
        {triangle ? (
          <div className="conv-board is-stencil">
            <TaxTriangle
              corners={corners}
              total={total}
              renderValue={c => (
                <Peek title={`${POOLS[c.key].label} accounts`} editor={() => <AssetEditor only={POOLS[c.key].tables} />}>
                  {c.value === null ? 'Not recorded' : money(c.value)}
                </Peek>
              )}
            />
            <SplitBar parts={CARDS.map(k => ({ key: k, label: POOLS[k].label, value: byKey[k]?.value || 0, fill: POOLS[k].fill }))} />
          </div>
        ) : (
          <div className="conv-board is-open">
            <PoolFigures corners={corners} total={total} />
            <SplitBar parts={CARDS.map(k => ({ key: k, label: POOLS[k].label, value: byKey[k]?.value || 0, fill: POOLS[k].fill }))} />
            <div className="conv-draw-room" aria-hidden="true" />
          </div>
        )}
        <Callouts items={callouts} />
      </div>

      <div className="conv-pools">
        {CARDS.map(k => {
          const pool = byKey[k];
          const share = total > 0 && pool ? Math.round((pool.value / total) * 100) : null;
          return (
            <section className="conv-pool-card" key={k} style={{ '--pool': POOLS[k].fill }}>
              <div className="conv-pool-card-head">
                <h3>{POOLS[k].label}</h3>
                <span>{share === null ? '' : `${share}%`}</span>
              </div>
              <ul className="conv-terms">
                {termsFor(k, pool).pros.map(t => <li className="is-pro" key={t}><span aria-hidden="true">+</span>{t}</li>)}
                {termsFor(k, pool).cons.map(t => <li className="is-con" key={t}><span aria-hidden="true">{'−'}</span>{t}</li>)}
              </ul>
            </section>
          );
        })}
      </div>
    </>
  );
}
